import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComponentLoader, Layout, MetricItemAdvance, WidgetDivider } from '@netapp/bxp-design-system-react';
import { SuccessIcon, NoticeTriangleIcon } from '@netapp/bxp-style/react-icons/Notification';
import { apiClient } from 'utils/clients';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import styles from '../../style.module.scss';
import { selectHasCertificate } from './proxyConfiguration';

const StatusMetric = ({ label, value, ValueIcon, valueIconColor }) => (
    <MetricItemAdvance
        className={styles.metricPaddingRight5}
        label={label}
        labelTooltip={label}
        valueSize="small"
        value={value}
        valueTooltip={value}
        ValueIcon={ValueIcon}
        valueIconColor={valueIconColor}
        valueClassName={styles.valueLineHeight24}
    />
);

const ProxyCardStatus = ({ onConfiguredChange }) => {
    const { t } = useTranslation(i18nNamespace);
    const [proxyConfig, setProxyConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        let isCancelled = false;

        const loadProxyConfiguration = async () => {
            try {
                const { data } = await apiClient.get(
                    `${process.env.REACT_APP_OCCM_ENDPOINT}system/config/proxy`,
                    { headers: { Accept: 'application/json' } }
                );
                if (!isCancelled) {
                    setProxyConfig(data);
                    setErrorMessage(null);
                }
            } catch {
                if (!isCancelled) {
                    setProxyConfig(null);
                    setErrorMessage(t('failed_to_fetch_proxy'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadProxyConfiguration();

        return () => {
            isCancelled = true;
        };
    }, [t]);

    const proxyType = proxyConfig?.proxy_type?.toUpperCase();
    const isExplicit = proxyType === 'EXPLICIT';
    const isTransparent = proxyType === 'TRANSPARENT';
    const isConfigured = isExplicit || isTransparent;

    useEffect(() => {
        if (!isLoading) {
            onConfiguredChange?.(isConfigured, proxyConfig, errorMessage);
        }
    }, [errorMessage, isConfigured, isLoading, onConfiguredChange, proxyConfig]);

    if (isLoading) {
        return <ComponentLoader className={styles.cardStatusLoader} />;
    }

    if (!isConfigured) {
        return null;
    }

    const httpProxy = proxyConfig?.proxy_url?.replace(/^(https?):\/\//i, '') || t('n/a');
    const hasCertificate = selectHasCertificate(proxyConfig);

    return (
        <Layout.Grid>
            <Layout.GridItem lg={4} className={styles.flexRowAlignEnd}>
                <StatusMetric
                    label={t('proxy_type')}
                    value={t(isExplicit ? 'explicit_proxy_type' : 'transparent_proxy_type')}
                    ValueIcon={SuccessIcon}
                    valueIconColor="success"
                />
                {isExplicit && <WidgetDivider expand />}
            </Layout.GridItem>
            {isExplicit && (
                <>
                    <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                        <StatusMetric
                            label={t('http_proxy')}
                            value={httpProxy}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                    <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                        <StatusMetric
                            label={t('certificate')}
                            value={t(hasCertificate ? 'uploaded' : 'not_uploaded')}
                            ValueIcon={hasCertificate ? SuccessIcon : NoticeTriangleIcon}
                            valueIconColor={hasCertificate ? 'success' : 'warning'}
                        />
                    </Layout.GridItem>
                </>
            )}
        </Layout.Grid>
    );
};

export default React.memo(ProxyCardStatus);
